import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import { AlertCircle, CheckCircle, Users, Loader, UserCheck, UserX, Mail, Calendar, RefreshCw } from 'lucide-react';

const AdminUsers = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [adminUsersData, setAdminUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSite, setExpandedSite] = useState(null);

  useEffect(() => {
    if (selectedSite) {
      fetchAdminUsers();
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [user, selectedSite]);

  // Auto-expand first site when data loads
  useEffect(() => {
    if (adminUsersData.length > 0 && expandedSite === null) {
      setExpandedSite(adminUsersData[0].site_id);
    }
  }, [adminUsersData]);

  const fetchAdminUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/analytics/admin-users.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setAdminUsersData(result.data);
      } else {
        setError(result.message || 'Failed to fetch admin users');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading admin users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="h-8 w-8 text-blue-600" />
              Admin Users
            </h1>
            <p className="text-gray-600 mt-2">Magento admin user accounts and access details</p>
          </div>
          <button
            onClick={fetchAdminUsers}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {adminUsersData.map((site) => (
          <div key={site.site_id} className="mb-6">
            <div className="bg-white rounded-lg shadow">
              {/* Site Header */}
              <div
                onClick={() => setExpandedSite(expandedSite === site.site_id ? null : site.site_id)}
                className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors rounded-t-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{site.site_name}</h3>
                    <p className="text-sm text-gray-500">
                      {site.stats?.total_users || 0} user(s)
                      {site.stats?.active_users > 0 && ` • ${site.stats.active_users} active`}
                      {site.stats?.inactive_users > 0 && ` • ${site.stats.inactive_users} inactive`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {site.error ? (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>
              </div>

              {expandedSite === site.site_id && (
                <div className="border-t border-gray-200">
                  {site.error ? (
                    <div className="p-6">
                      <div className="rounded-lg bg-red-50 p-4 flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-red-900">Error</p>
                          <p className="text-sm text-red-700">{site.error}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 space-y-6">
                      {/* Quick Stats */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Overview
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Users className="h-4 w-4 text-blue-600" />
                              <p className="text-xs text-blue-600 uppercase font-medium">Total Users</p>
                            </div>
                            <p className="text-2xl font-bold text-blue-900">{site.stats?.total_users || 0}</p>
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <UserCheck className="h-4 w-4 text-green-600" />
                              <p className="text-xs text-green-600 uppercase font-medium">Active</p>
                            </div>
                            <p className="text-2xl font-bold text-green-900">{site.stats?.active_users || 0}</p>
                          </div>
                          <div className="bg-red-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <UserX className="h-4 w-4 text-red-600" />
                              <p className="text-xs text-red-600 uppercase font-medium">Inactive</p>
                            </div>
                            <p className="text-2xl font-bold text-red-900">{site.stats?.inactive_users || 0}</p>
                          </div>
                        </div>
                      </div>

                      {/* Users Table */}
                      {Array.isArray(site.users) && site.users.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            User Accounts
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Username
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Last Modified
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {site.users.map((adminUser) => (
                                  <tr key={adminUser.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm">
                                      <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                          <span className="text-blue-600 font-medium text-sm">
                                            {(adminUser.firstname?.[0] || '') + (adminUser.lastname?.[0] || '')}
                                          </span>
                                        </div>
                                        <div>
                                          <p className="font-medium text-gray-900">
                                            {adminUser.firstname} {adminUser.lastname}
                                          </p>
                                          {adminUser.interface_locale && (
                                            <p className="text-xs text-gray-500">
                                              Locale: {adminUser.interface_locale}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{adminUser.username}</code>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      <a
                                        href={`mailto:${adminUser.email}`}
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                      >
                                        <Mail className="h-3 w-3" />
                                        {adminUser.email}
                                      </a>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {adminUser.is_active ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          <UserCheck className="h-3 w-3 mr-1" />
                                          Active
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                          <UserX className="h-3 w-3 mr-1" />
                                          Inactive
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                      <div className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {formatDate(adminUser.created)}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                      <div className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {formatDate(adminUser.modified)}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {(!site.users || site.users.length === 0) && !site.error && (
                        <div className="rounded-lg bg-yellow-50 p-4 border border-yellow-200">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-yellow-900">No admin users found</p>
                              <p className="text-xs text-yellow-700 mt-1">
                                This may indicate insufficient API permissions to access admin user data.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Info Box */}
                      <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">About this data</p>
                            <p>This information is retrieved directly from your Magento instance via REST API. The API integration account needs appropriate permissions to access admin user data.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminUsers;
